import { LitElement, html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import globalStyles from './styles/outline-search.global.pcss?inline';
import innerStyles from './styles/outline-search.inner.pcss?inline';
import { OutlineMainHeader } from '../outline-main-header/outline-main-header';
import '../outline-container/outline-container';
import { Loader } from '@googlemaps/js-api-loader';

export const searchVariants = ['simple', 'nav'] as const;
export type SearchVariant = (typeof searchVariants)[number];

@customElement('outline-search')
export class OutlineSearch extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  @property({ type: String }) variant: SearchVariant;
  @state() searchOpen = true;
  private maxChar = 3;

  render(): TemplateResult {
    return html`
      <section class="outline-search">
        ${this.variant === 'nav' ? this.navTemplate() : this.defaultTemplate()}
      </section>
    `;
  }

  navTemplate() {
    return html`
      <outline-container>
        <div class="outline-search--nav-wrapper">
          <div class="outline-search--main">
            <div class="outline-search--top-line">
              <div class="outline-search--heading-wrapper">
                <h2 class="outline-search--label">Search</h2>
                <button
                  class="outline-search--close-button"
                  @click=${this.buttonCloseHandler}
                  aria-label="close search panel"
                  aria-controls="outline-main-header--search-panel"
                  aria-expanded=${this.searchOpen ? 'true' : 'false'}
                  type="button"
                ></button>
              </div>
              <div class="outline-search--sub-heading">
                <slot name="subheading"></slot>
              </div>
              <div class="outline-search--form-wrapper">
                ${this.defaultTemplate()}
              </div>
            </div>
          </div>
          <div class="outline-search--supplemental">
            <div class="outline-search--supplemental-title">
              <slot name="supplement-title"></slot>
            </div>
            <div class="outline-search--supplemental-content">
              <slot name="supplement-content"></slot>
            </div>
          </div>
        </div>
      </outline-container>
    `;
  }

  defaultTemplate() {
    return html`<slot name="form"></slot>`;
  }

  buttonCloseHandler() {
    const nav = document.querySelector(
      'outline-main-header'
    ) as OutlineMainHeader;
    if (nav) {
      this.searchOpen = false;
      nav.searchOpen = false;
    }
  }

  async firstUpdated() {
    await this.ensureGoogleMapsLoaded();

    const input = this.querySelector(
      'input[name="city_zip"]'
    ) as HTMLInputElement | null;
    if (!input) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'geolocationWrapper';
    input.parentElement?.insertBefore(wrapper, input);
    wrapper.appendChild(input);

    const resultsList = document.createElement('ul');
    resultsList.className = 'autocomplete-results';
    wrapper.appendChild(resultsList);

    const autocomplete = new google.maps.places.AutocompleteService();
    const placesService = new google.maps.places.PlacesService(
      document.createElement('div')
    );

    input.addEventListener('input', () => {
      const value = input.value.trim();
      resultsList.innerHTML = '';

      if (value.length < this.maxChar) return;

      autocomplete.getPlacePredictions(
        {
          input: value,
          types: ['(regions)'],
          componentRestrictions: { country: 'us' },
        },
        (predictions, status) => {
          if (
            status !== google.maps.places.PlacesServiceStatus.OK ||
            !predictions
          )
            return;

          predictions.forEach(prediction => {
            const li = document.createElement('li');
            li.textContent = prediction.description;
            li.className = 'autocomplete-item';

            li.addEventListener('click', () => {
              placesService.getDetails(
                {
                  placeId: prediction.place_id,
                  fields: ['address_components'],
                },
                async (place, status) => {
                  if (
                    status !== google.maps.places.PlacesServiceStatus.OK ||
                    !place?.address_components
                  )
                    return;

                  await this.formatInputValue(input, place.address_components);
                  resultsList.innerHTML = '';
                }
              );
            });

            resultsList.appendChild(li);
          });
        }
      );
    });

    const form = input.closest('form') as HTMLFormElement | null;
    if (form) {
      let resubmitting = false;
      form.addEventListener(
        'submit',
        async e => {
          if (resubmitting) return;
          e.preventDefault();
          await this.formatInputValue(input);
          resubmitting = true;
          form.submit();
        },
        { capture: true }
      );
    }

    document.addEventListener('click', event => {
      if (!wrapper.contains(event.target as Node)) {
        resultsList.innerHTML = '';
      }
    });
  }

  private async formatInputValue(
    input: HTMLInputElement,
    components?: google.maps.GeocoderAddressComponent[]
  ): Promise<void> {
    const value = input.value.trim();
    if (!value) return;

    const isZip = /^\d{5}$/.test(value);
    if (!isZip && value.match(/.+, [A-Z]{2} \d{5}$/)) return;

    if (components) {
      this.setFormattedValue(input, components, isZip);
      return;
    }
    const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(value)}&key=AIzaSyDoJh7YPP3KfJF881UP9JhMDQFXmsoWdTE`;

    try {
      const response = await fetch(geocodeUrl);
      const data = await response.json();

      if (!data.results?.length) return;

      const components = data.results[0].address_components;
      this.setFormattedValue(input, components, isZip);
    } catch (error) {
      console.error('Geocode lookup failed:', error);
    }
  }

  private setFormattedValue(
    input: HTMLInputElement,
    components: google.maps.GeocoderAddressComponent[],
    isZip: boolean
  ): void {
    let city = '',
      state = '',
      zip = '';

    for (const comp of components) {
      const types = comp.types;
      if (
        types.includes('locality') ||
        types.includes('postal_town') ||
        types.includes('sublocality')
      ) {
        city = comp.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        state = comp.short_name;
      } else if (types.includes('postal_code')) {
        zip = comp.long_name;
      }
    }

    if (isZip && !city && zip && state) {
      input.value = `${state} ${zip}`;
    } else {
      const formatted = [city, state, zip].filter(Boolean).join(', ');
      if (formatted) {
        input.value = formatted;
      }
    }
  }

  private async ensureGoogleMapsLoaded(): Promise<void> {
    if (window.google?.maps?.places?.AutocompleteService) return;

    if (!(window as any)._googleMapsPromise) {
      const loader = new Loader({
        apiKey: import.meta.env.VITE_DEVELOPMENT_GOOGLE_MAP_API_KEY,
        version: 'weekly',
        libraries: ['places'],
      });
      (window as any)._googleMapsPromise = loader.load();
    }

    await (window as any)._googleMapsPromise;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
